import { useEffect, useState } from "react";
import {
  HeadingGroup,
  OffCanvasLayout,
  SearchFilters,
  Table,
} from "@wac-ui-dashboard/wac_component_library";
import { useDebounce } from "../../utils/hooks/useDebounce";
import BulkUpload from "./BulkUpload";
import { FILTERS, useThumbnailList } from "./useMenuThumbnails";
import { getRatioLabel } from "./ratio";
import Style from "./menuThumbnails.module.scss";

const TABLE_FIELDS = {
  image_url: { label: "Thumbnail", sortable: false },
  title: { label: "Title", sortable: false },
  file_name: { label: "File Name", sortable: false },
  type_label: { label: "Type", sortable: false },
  dimensions: { label: "Dimensions", sortable: false },
  size: { label: "Size", sortable: false },
  uploaded_date: { label: "Uploaded Date", sortable: false },
};

const formatSize = (bytes) => {
  if (!bytes) return "-";
  if (bytes >= 1024 * 1024) return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
  return `${Math.max(1, Math.round(bytes / 1024))} KB`;
};

const MenuThumbnails = () => {
  const [activeFilter, setActiveFilter] = useState("all");
  const [search, setSearch] = useState("");
  const [showBulkUpload, setShowBulkUpload] = useState(false);
  const [clearSelection, setClearSelection] = useState(false);

  // wait for the user to stop typing before asking the API (it searches title and file name)
  const debouncedSearch = useDebounce(search.trim(), 400);

  const filter = FILTERS.find((item) => item.id === activeFilter) ?? FILTERS[0];
  const { items, counts, isLoading, refetch, removeThumbnails } = useThumbnailList({
    type: filter.type,
    search: debouncedSearch,
  });

  // the rows change with the search and type, so drop a selection of rows that may no longer show
  useEffect(() => {
    setClearSelection(true);
  }, [activeFilter, debouncedSearch]);

  const countFor = (id) =>
    id === "all"
      ? Object.values(counts).reduce((total, count) => total + (count ?? 0), 0)
      : counts[id] ?? 0;

  const handleDelete = async (ids) => {
    if (!ids?.length) return;
    const deleted = await removeThumbnails(ids);
    if (deleted) setClearSelection(true);
  };

  const getRow = (field, data) => {
    switch (field) {
      case "image_url":
        return <img className={Style.table_thumb} src={data.image_url} alt={data.title} />;
      case "type_label":
        return (
          <span className={`${Style.type_badge} ${Style[data.type_label?.toLowerCase()]}`}>
            {data.type_label}
          </span>
        );
      case "dimensions":
        return (
          <p className="pro-mb-0 pro-nowrap">
            {data.width && data.height
              ? `${data.width} × ${data.height} (${getRatioLabel(data.width, data.height)})`
              : "-"}
          </p>
        );
      case "size":
        return <p className="pro-mb-0">{formatSize(data.size)}</p>;
      default:
        return <p className="pro-mb-0">{data[field] || "-"}</p>;
    }
  };

  return (
    <>
      <HeadingGroup title={"Menu Thumbnails"} extraClassName={`pro-mb-4`}>
        <button
          type="button"
          className={`pro-btn pro-btn-primary pro-items-center`}
          onClick={() => setShowBulkUpload(true)}
        >
          <span className="material-symbols-outlined">upload_file</span>
          <span>Bulk Upload</span>
        </button>
      </HeadingGroup>

      <div className="col-12 pro-pb-6">
        <div className="pro-mb-3">
          <SearchFilters
            onSearchInput={(e) => setSearch(e.target.value)}
            searchInputProps={{ value: search, placeholder: "Search by title or file name" }}
            SearchIcon={<span className="material-symbols-outlined"> search </span>}
            showActions={false}
            showDateRange={false}
          />
        </div>
        <div className={`pro-mb-4 ${Style.filters}`}>
          {FILTERS.map((item) => (
            <button
              key={item.id}
              type="button"
              className={`pro-btn pro-btn-outline ${activeFilter === item.id ? Style.active : ""}`}
              onClick={() => setActiveFilter(item.id)}
            >
              {item.label} ({countFor(item.id)})
            </button>
          ))}
        </div>

        <Table
          data={items}
          uniqueID={"_id"}
          fields={TABLE_FIELDS}
          getRow={getRow}
          showCheckBox={true}
          multiSelect={true}
          editable={false}
          assignable={false}
          deletable={true}
          handleDelete={handleDelete}
          handleSelect={() => setClearSelection(false)}
          clear={clearSelection}
          deleteIcon={<span className="material-symbols-outlined">delete</span>}
          loading={isLoading}
          perpage={10}
        />
        {!isLoading && items.length === 0 && (
          <p className="pro-font-sm pro-opacity-60 pro-text-center pro-mt-4">
            {debouncedSearch
              ? `No thumbnails match "${debouncedSearch}".`
              : "No thumbnails yet. Use Bulk Upload to add menu thumbnails."}
          </p>
        )}
      </div>

      <OffCanvasLayout
        show={showBulkUpload}
        handleClose={() => setShowBulkUpload(false)}
        title={"Bulk Upload Menu Thumbnails"}
        closeIcon={<span className="material-symbols-outlined"> close </span>}
        backdrop={"static"}
      >
        {showBulkUpload && (
          <BulkUpload refetch={refetch} handleClose={() => setShowBulkUpload(false)} />
        )}
      </OffCanvasLayout>
    </>
  );
};

export default MenuThumbnails;
